import { useAccountState } from '../../../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikProps } from 'formik';
import {
    Field,
    InfoLabel,
    Input,
    Link,
    Switch,
    Text,
    Textarea,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import { BookInformationRegular } from '@fluentui/react-icons';
import { useAccess } from '../../../../auth/useAccess';
import { BrandCard, Row, Column, Paths } from '../../../common';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { Case, CaseStatus, Contact, Party, PartyWithContacts } from '../../../../data/types';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { calendarDayGridClassNames } from '@fluentui/react-calendar-compat';
import { PartyDropdown } from '../../../controls/Comboboxes/PartyDropdown';
import { PartyControl } from '../../../controls/PartyControl';
import { useNavigate } from 'react-router';
import { SettingsPageTabs } from '../../Settings/AccountSettingsPage';
import { InputCurrency } from '../../../controls/InputCurrency';
import { CaseTypeComboBox } from '../../../controls/Comboboxes/CaseTypeComboBox';
import { CaseStatusDropdown } from '../../../controls/Comboboxes/CaseStatusDropdown';
import { useUtilities } from '../../../../utilities/useUtilities';
import { usePartyService } from '../../../../data/services';
import { useValidation } from '../../../../data/types/validation';

const useStyles = makeStyles({
    root: {},
    field: {
        maxWidth: '100%',
    },
    emptyButRequired: {
        backgroundColor: tokens.colorBrandBackground2,
    },
    currency: {
        width: 'auto',
        maxWidth: '10rem',
    },
    estimatedHours: {
        maxWidth: '12rem',
    },
    retainedDateCalendar: {
        [`& .${calendarDayGridClassNames.weekRow}`]: {
            backgroundColor: tokens.colorNeutralBackground1,
        },
        [`& .${calendarDayGridClassNames.weekDayLabelCell}`]: {
            backgroundColor: tokens.colorNeutralBackground1,
        },
    },
});
export type CaseInfoCardProps = {
    className?: string;
    caseForm?: FormikProps<Case>;
    createMode?: boolean;
    editMode?: boolean;
    caseTypeClassName?: string;
    shortDescriptionClassName?: string;
    overviewClassName?: string;
    statusClassName?: string;
    retainedDateClassName?: string;
};
export const CaseInfoCard = (props: CaseInfoCardProps) => {
    const {
        className,
        caseForm,
        createMode = false,
        editMode = false,
        caseTypeClassName,
        shortDescriptionClassName,
        overviewClassName,
        statusClassName,
        retainedDateClassName,
    } = props;

    const caseObj = caseForm?.values as Case;

    //Hooks
    const { primaryAccount, getUserName } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const partyService = usePartyService();
    const { validateCaseObj } = useValidation();
    const navTo = useNavigate();
    const utils = useUtilities();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const cardClasses = mergeClasses(styles.root, className);
    const caseNameClasses = mergeClasses(!caseObj.caseName && styles.emptyButRequired);
    const retainedDateClasses = mergeClasses(
        styles.retainedDateCalendar,
        retainedDateClassName,
        !caseObj.retainedDate && styles.emptyButRequired,
    );
    const retainedByClasses = mergeClasses(!caseObj.retainedBy?.party?.name && styles.emptyButRequired);
    const caseTypeClasses = mergeClasses(caseTypeClassName);
    const fieldClasses = mergeClasses(commonStyles.text.field, styles.field);

    //Access
    const canEdit = () => {
        if (!!createMode && access.account.canCreateCases()) {
            return true;
        }

        if (!!editMode && access.cases.canEditCaseInfo(caseObj?.caseId)) {
            return true;
        }
        return false;
    };

    //Logic
    const getParty = () => {
        return (
            partyService.findParty(caseForm?.values.retainedBy?.party?.partyId as string) ||
            caseForm?.values.retainedBy?.party
        );
    };

    //UI Events
    const routeToCaseData = async (e?: React.MouseEvent) => {
        !!e && e.stopPropagation();

        //quiet save of form data
        await caseForm?.submitForm();

        //route to case types
        navTo(Paths.accountSettings, { state: { tab: SettingsPageTabs.caseData } });
    };
    const onCaseTypeSelected = (selValue?: string) => {
        caseForm?.setFieldValue('caseType', selValue);
    };
    const onCaseStatusSelected = (selValue: CaseStatus) => {
        caseForm?.setFieldValue('status', selValue);
    };
    const onRetainedDateSelected = (retDate: Date | null | undefined) => {
        caseForm?.setFieldValue('retainedDate', retDate?.toLocaleDateString());
    };
    const onClientSelected = (party?: Party) => {
        const clientWithContacts: PartyWithContacts = {
            party: party,
            contacts: [],
        };
        //console.log(party);

        caseForm?.setFieldValue('retainedBy', clientWithContacts);
    };
    const onClientContactAddOrChange = (addContacts: Contact[]) => {
        let retainedContacts = [...(caseForm?.values?.retainedBy?.contacts || [])];

        addContacts.forEach((contact) => {
            const ind = retainedContacts.findIndex((x) => x.contactId == contact.contactId);
            if (ind >= 0) {
                retainedContacts[ind] = contact;
            } else {
                retainedContacts = [...retainedContacts.filter((x) => x.contactId != contact.contactId), contact];
            }
        });

        caseForm?.setFieldValue('retainedBy.contacts', retainedContacts);
    };
    const onRemoveClientContact = (remContact: Contact) => {
        if (!!remContact) {
            const filteredContacts = caseForm?.values.retainedBy?.contacts.filter(
                (x) => x.contactId != remContact.contactId,
            );
            caseForm?.setFieldValue('retainedBy.contacts', filteredContacts);
        }
    };
    const onIsFixedFeeChanged = (ev: React.ChangeEvent<HTMLInputElement>) => {
        ev.stopPropagation();

        const isFixedFee = ev.currentTarget.checked;
        caseForm?.setFieldValue('isFixedFee', isFixedFee);
    };
    const onFixedFeeValueChanged = (value: string) => {
        caseForm?.setFieldValue('fixedAmount', value);
    };

    return (
        <>
            <BrandCard
                className={cardClasses}
                title='Case Info'
                headerIcon={<BookInformationRegular />}
                height='auto'
                minHeight='fit-content'
                minWidth='fit-content'
                data-testid='case-info-card'
            >
                <Row gap='large' height='auto'>
                    <Column flex='2 1 325px' padding='px-1'>
                        {/* Case Name */}
                        <Field
                            label='Case Name'
                            required={!!canEdit()}
                            className={fieldClasses}
                            validationMessage={caseForm?.errors.caseName}
                        >
                            <Input
                                name='caseName'
                                className={caseNameClasses}
                                value={caseForm?.values.caseName}
                                onChange={caseForm?.handleChange}
                                tabIndex={1}
                                placeholder='Enter case name'
                                readOnly={!canEdit()}
                                appearance={!canEdit() ? 'underline' : undefined}
                                data-testid='case-info-card-name-input'
                                maxLength={validateCaseObj.caseName.maxLength}
                            />
                        </Field>

                        {/* Case Type */}
                        <Field
                            className={fieldClasses}
                            label={
                                <InfoLabel
                                    label='Type'
                                    info={
                                        <Text size={200}>
                                            Choose from a list of case types defined for your account.
                                            {access.settings.canEditCaseDataTypes() && (
                                                <Text>
                                                    {' '}
                                                    <Link
                                                        onClick={routeToCaseData}
                                                        data-testid='case-info-card-add-types-link'
                                                    >
                                                        Click here
                                                    </Link>{' '}
                                                    to add /edit these.
                                                </Text>
                                            )}
                                        </Text>
                                    }
                                />
                            }
                        >
                            {!!canEdit() ? (
                                <CaseTypeComboBox
                                    className={caseTypeClasses}
                                    value={caseObj?.caseType}
                                    tabIndex={2}
                                    onCaseTypeSelected={onCaseTypeSelected}
                                    routeToCaseTypes={routeToCaseData}
                                />
                            ) : (
                                <Input
                                    tabIndex={2}
                                    readOnly
                                    appearance='underline'
                                    value={caseObj?.caseType}
                                    data-testid='case-info-card-type-readonly-input'
                                />
                            )}
                        </Field>

                        {/* Case Status */}
                        <Field
                            className={fieldClasses}
                            label={
                                <InfoLabel
                                    label='Status'
                                    info={
                                        <Text size={200}>
                                            Choose from a list of case statuses defined for your account.
                                            {access.settings.canEditCaseDataTypes() && (
                                                <Text>
                                                    {' '}
                                                    <Link
                                                        onClick={routeToCaseData}
                                                        data-testid='case-info-card-add-statuses-link'
                                                    >
                                                        Click here
                                                    </Link>{' '}
                                                    to add /edit these.
                                                </Text>
                                            )}
                                        </Text>
                                    }
                                />
                            }
                        >
                            {!!canEdit() ? (
                                <CaseStatusDropdown
                                    className={statusClassName}
                                    status={caseObj?.status}
                                    allowedStatuses={caseObj?.allowedStatuses}
                                    tabIndex={3}
                                    onCaseStatusSelected={onCaseStatusSelected}
                                />
                            ) : (
                                <Input
                                    tabIndex={3}
                                    readOnly
                                    appearance='underline'
                                    value={caseObj?.status?.status}
                                    data-testid='case-info-card-status-readonly-input'
                                />
                            )}
                        </Field>

                        {/* Billing */}
                        <Field label='Billing' className={fieldClasses}>
                            <Column noGap>
                                <Column align='center' vAlign='middle' padding='pl-3'>
                                    <Row noWrap gap='small'>
                                        <Column width='auto' align='center' vAlign='middle'>
                                            <Text weight={!caseObj?.isFixedFee ? 'semibold' : 'regular'}>Billed Hourly</Text>
                                        </Column>
                                        <Column vAlign='middle'>
                                            <Switch
                                                label={
                                                    <Column height='100%' vAlign='middle' padding='px-1'>
                                                        <Text weight={caseObj?.isFixedFee ? 'semibold' : 'regular'}>
                                                            Fixed Fee
                                                        </Text>
                                                    </Column>
                                                }
                                                checked={caseObj?.isFixedFee}
                                                onChange={onIsFixedFeeChanged}
                                                disabled={!canEdit()}
                                                data-testid='case-info-card-fixed-fee-switch'
                                            />
                                        </Column>
                                    </Row>
                                </Column>
                                <Column vAlign='middle' padding='px-3'>
                                    {!!caseObj?.isFixedFee && (
                                        <Field
                                            label='Fixed Fee'
                                            className={fieldClasses}
                                            validationMessage={caseForm?.errors?.fixedAmount}
                                        >
                                            <InputCurrency
                                                className={styles.currency}
                                                name='fixedAmount'
                                                value={caseObj?.fixedAmount}
                                                onChange={(_e, data) => {
                                                    onFixedFeeValueChanged(data.value);
                                                }}
                                                readOnly={!canEdit()}
                                                data-testid='case-info-card-fixed-fee-input'
                                            />
                                        </Field>
                                    )}
                                    {!caseObj?.isFixedFee && (
                                        <Field
                                            label='Estimated Hours'
                                            className={fieldClasses}
                                            validationMessage={caseForm?.errors.estimatedHours}
                                        >
                                            <Input
                                                name='estimatedHours'
                                                className={styles.estimatedHours}
                                                value={caseObj?.estimatedHours || ''}
                                                onChange={caseForm?.handleChange}
                                                tabIndex={4}
                                                placeholder='Enter estimated hours'
                                                readOnly={!canEdit()}
                                                maxLength={6}
                                                appearance={!canEdit() ? 'underline' : undefined}
                                                contentAfter={<Text>hrs</Text>}
                                                data-testid='case-info-card-estimated-hours-input'
                                            />
                                        </Field>
                                    )}
                                </Column>
                            </Column>
                        </Field>
                    </Column>

                    <Column flex='2 1 325px' padding='px-1 pb-2'>
                        {/* Retained Date */}
                        <Field label='Retained Date' required={createMode} size='medium' className={fieldClasses}>
                            <DatePicker
                                name='retainedDate'
                                className={retainedDateClasses}
                                calendar={{ className: styles.retainedDateCalendar }}
                                value={!!caseForm?.values.retainedDate ? new Date(caseForm?.values.retainedDate) : null}
                                onSelectDate={onRetainedDateSelected}
                                placeholder='Select a date...'
                                open={!canEdit() ? false : undefined}
                                readOnly={!canEdit()}
                                appearance={!canEdit() ? 'underline' : undefined}
                                minDate={validateCaseObj.retainedDate.min}
                                maxDate={validateCaseObj.retainedDate.max}
                                data-testid='case-retained-date-picker'
                            />
                        </Field>

                        {/* Retained By (Client) */}
                        <Field
                            label={
                                <InfoLabel
                                    label='Retained By'
                                    info='This is the client for which you are doing the work, and to whom you will send invoices.'
                                />
                            }
                            required={createMode}
                            className={fieldClasses}
                        >
                            {createMode && (
                                <PartyDropdown
                                    className={retainedByClasses}
                                    onPartySelected={onClientSelected}
                                    appearance='outline'
                                />
                            )}
                            {!!caseForm?.values.retainedBy?.party?.partyId && (
                                <Column align='center' padding='py-2' height='auto'>
                                    <PartyControl
                                        size='large'
                                        party={getParty()}
                                        contacts={caseForm?.values.retainedBy.contacts}
                                        addContacts={canEdit()}
                                        removeContacts={canEdit()}
                                        onContactsAddOrChange={onClientContactAddOrChange}
                                        onContactRemoved={onRemoveClientContact}
                                    />
                                </Column>
                            )}
                        </Field>
                    </Column>

                    <Column flex='3 1 325px' padding='px-1' minWidth='325px'>
                        {/* Short Description */}
                        <Field
                            label={caseObj.shortDescription?.fieldName || 'Description / Action / Proceeding'}
                            className={fieldClasses}
                            hint={`${caseObj.shortDescription?.fieldValue?.substring(0, validateCaseObj.shortDescription.maxLength).length || 0}/${validateCaseObj.shortDescription.maxLength}`}
                        >
                            <Textarea
                                name='shortDescription.fieldValue'
                                className={shortDescriptionClassName}
                                tabIndex={4}
                                rows={3}
                                maxLength={validateCaseObj.shortDescription.maxLength}
                                readOnly={!canEdit()}
                                value={
                                    (caseObj.shortDescription?.fieldValue?.substring(
                                        0,
                                        validateCaseObj.shortDescription.maxLength,
                                    ) as string) || ''
                                }
                                onChange={caseForm?.handleChange}
                                placeholder={`Enter ${caseObj.shortDescription?.fieldName || 'Description / Action / Proceeding'}`}
                                data-testid='case-info-card-short-description-input'
                            />
                        </Field>

                        {/* Overview */}
                        <Field
                            label='Overview'
                            size='medium'
                            className={fieldClasses}
                            hint={`${caseObj.overview?.substring(0, validateCaseObj.overview.maxLength).length || 0}/${validateCaseObj.overview.maxLength}`}
                        >
                            <Textarea
                                name='overview'
                                className={overviewClassName}
                                rows={8}
                                maxLength={validateCaseObj.overview.maxLength}
                                readOnly={!canEdit()}
                                appearance={!canEdit() ? 'outline' : undefined}
                                value={(caseObj.overview?.substring(0, validateCaseObj.overview.maxLength) as string) || ''}
                                onChange={caseForm?.handleChange}
                                placeholder='Enter an overview of this case'
                                data-testid='case-info-card-overview-input'
                            />
                        </Field>
                    </Column>
                </Row>
                {!!caseObj.lifecycle?.createdOn && !!caseObj.lifecycle?.createdBy && (
                    <Column vAlign='bottom'>
                        <Text className={commonStyles.text.subtleText} size={200} italic>
                            Created:{' '}
                            {utils.date.formatDate(
                                caseObj.lifecycle?.createdOn,
                                { month: 'long', day: 'numeric', year: 'numeric' },
                                true,
                            )}
                            , by {getUserName(caseObj.lifecycle?.createdBy as string)}
                        </Text>
                    </Column>
                )}
            </BrandCard>
        </>
    );
};
